import { ReactNode } from "react";
import { Box, Grid, Paper, Stack, Typography, alpha } from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ReplayIcon from "@mui/icons-material/Replay";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";

import { infoActivacionVisita } from "./estadoActivacionVisita";

const ACCENT = "#1565c0";

function estadoUpper(r: any): string {
    return (r?.estado_agendamiento || "").toString().toUpperCase();
}

function Metric({
    icon,
    label,
    value,
    sub,
    color = ACCENT,
}: {
    icon: ReactNode;
    label: string;
    value: ReactNode;
    sub?: string;
    color?: string;
}) {
    return (
        // 2 por fila en móvil, 3 en tablet y las 5 repartidas (flex igual) en
        // desktop — sin huecos y con ancho suficiente para etiquetas largas.
        <Grid item xs={6} sm={4} md>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1.25, sm: 1.5 },
                    height: "100%",
                    minWidth: 0,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: alpha(color, 0.03),
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ mb: 0.5, minWidth: 0 }}
                >
                    <Box sx={{ color, display: "flex", flexShrink: 0 }}>{icon}</Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{ lineHeight: 1.2, minWidth: 0 }}
                    >
                        {label}
                    </Typography>
                </Stack>
                <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ lineHeight: 1.1, fontSize: { xs: "1.15rem", sm: "1.25rem" } }}
                >
                    {value}
                </Typography>
                {sub && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", lineHeight: 1.2 }}
                    >
                        {sub}
                    </Typography>
                )}
            </Paper>
        </Grid>
    );
}

/**
 * Encabezado de KPIs de Calidad de Visitas. Como la lista es por AGENDAMIENTO
 * (las visitas aparecen desde su creación, antes de tener Orden de Trabajo),
 * las métricas reflejan el ciclo de vida del agendamiento sobre las filas
 * cargadas (muestra actual, según paginación/filtros).
 */
export default function KpisCalidadVisitas({ rows }: { rows: any[] }) {
    const total = rows.length;
    const enEspera = rows.filter((r) =>
        ["EN ESPERA", "EN PAUSA"].includes(estadoUpper(r))
    ).length;
    const agendadas = rows.filter((r) => estadoUpper(r) === "FINALIZADO").length;
    const conTecnico = rows.filter((r) => !!(r as any)?.id_orden_trabajo).length;
    const recoordinadas = rows.filter((r) => estadoUpper(r) === "RECORDINADO").length;
    // Activaciones pendientes: visitas cuya OT requiere cambio de ONT y aún no
    // se gestiona (EN ESPERA). Las que no requieren ONT ya no cuentan porque el
    // técnico las marca GESTIONADA al finalizar.
    const activacionesPendientes = rows.filter(
        (r) => infoActivacionVisita(r).pendiente
    ).length;

    const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Resumen de calidad{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                    (muestra actual: {total} visitas)
                </Typography>
            </Typography>
            <Grid container spacing={1.5}>
                <Metric
                    icon={<AssignmentTurnedInIcon fontSize="small" />}
                    label="Total"
                    value={total}
                    sub="visitas en vista"
                />
                <Metric
                    icon={<HourglassEmptyIcon fontSize="small" />}
                    label="En espera"
                    value={enEspera}
                    sub={`${pct(enEspera)}% del total`}
                    color="#ed6c02"
                />
                <Metric
                    icon={<EventAvailableIcon fontSize="small" />}
                    label="Agendadas"
                    value={agendadas}
                    sub={`${pct(agendadas)}% del total`}
                    color="#2e7d32"
                />
                <Metric
                    icon={<EngineeringIcon fontSize="small" />}
                    label="Con técnico (OT)"
                    value={conTecnico}
                    sub={`${pct(conTecnico)}% del total`}
                    color="#0288d1"
                />
                <Metric
                    icon={<ReplayIcon fontSize="small" />}
                    label="Recoordinadas"
                    value={recoordinadas}
                    sub={`${pct(recoordinadas)}% del total`}
                    color="#9c27b0"
                />
                <Metric
                    icon={<SettingsInputAntennaIcon fontSize="small" />}
                    label="Activaciones pend."
                    value={activacionesPendientes}
                    sub={`${pct(activacionesPendientes)}% del total`}
                    color="#d32f2f"
                />
            </Grid>
        </Box>
    );
}
